import { useState } from 'react';
import { Bluetooth, Check, LocateFixed, MapPin } from 'lucide-react';
import Modal, { ModalBody } from '../ui/Modal';
import { LynxLocation } from '../../lib/proximity/native/LynxLocation';

/**
 * The one place to see what Find can use (operator, 2026-10-04 "Permissions B"): a round button whose dot is green
 * when everything is on and amber when something is off, opening a sheet with one row each for Location, Precise
 * Location and Bluetooth. It doubles as the QA check, so it shows when all is well too. A capability the phone
 * lacks (UWB, Bluetooth "unsupported") is not a row - absent things are not mentioned.
 *
 * "Turn on" opens Prii's page in iOS Settings (UIApplication.openSettingsURLString), except a first location ask,
 * which shows the iOS prompt. Bluetooth switched off phone-wide cannot be deep-linked, so it only says so.
 */

// Dev only: ?perms=ok|location|precise|bluetooth|bt-denied shows a state the browser can't produce.
function devOverride() {
  if (!import.meta.env.DEV || typeof window === 'undefined') return null;
  return new URLSearchParams(window.location.search).get('perms');
}

export function permissionRows({
  locationAuth,
  locationLost = false,
  preciseOff = false,
  bleState,
}) {
  const forced = devOverride();
  if (forced) {
    locationAuth = forced === 'location' ? 'denied' : 'whenInUse';
    locationLost = false;
    preciseOff = forced === 'precise';
    bleState =
      forced === 'bluetooth' ? 'poweredOff' : forced === 'bt-denied' ? 'unauthorized' : 'poweredOn';
  }
  const asked = !['notDetermined', 'unknown', 'pending', undefined, null].includes(locationAuth);
  const locationOn = ['whenInUse', 'always'].includes(locationAuth) && !locationLost;
  const rows = [
    {
      key: 'location',
      label: 'Location',
      Icon: MapPin,
      on: locationOn,
      action: locationOn ? null : asked ? 'settings' : 'ask',
    },
  ];
  // Precise only means something once location is on.
  if (locationOn)
    rows.push({
      key: 'precise',
      label: 'Precise Location',
      Icon: LocateFixed,
      on: !preciseOff,
      action: preciseOff ? 'settings' : null,
    });
  if (bleState === 'poweredOn' || bleState === 'poweredOff' || bleState === 'unauthorized') {
    rows.push({
      key: 'bluetooth',
      label: 'Bluetooth',
      Icon: Bluetooth,
      on: bleState === 'poweredOn',
      action: bleState === 'unauthorized' ? 'settings' : null,
      hint: bleState === 'poweredOff' ? 'Turn on Bluetooth' : null,
    });
  }
  return rows;
}

export default function PermissionsStatus({
  rows,
  onAskLocation,
  className = 'maps-page__back',
  open: openProp,
  onOpenChange,
}) {
  // Controlled when the host opens it on its own (Find, when location is lost); otherwise it keeps its own state.
  const [openState, setOpenState] = useState(false);
  const open = openProp ?? openState;
  const setOpen = onOpenChange ?? setOpenState;
  const allOn = rows.every((r) => r.on);
  const firstOff = rows.find((r) => !r.on);
  // Friends see you on GPS alone; Bluetooth only adds close range. So the title follows Location + Precise.
  const visible = rows.filter((r) => r.key !== 'bluetooth').every((r) => r.on);
  const openSettings = () => {
    void LynxLocation.openAppSettings().catch(() => {});
  };
  return (
    <>
      <button
        type="button"
        className={`${className} perm-status`}
        aria-label={allOn ? 'Location and Bluetooth on' : `${firstOff.label} off`}
        onClick={() => setOpen(true)}
      >
        <MapPin size={18} aria-hidden="true" />
        <span
          className={`perm-status__dot${allOn ? '' : ' perm-status__dot--off'}`}
          aria-hidden="true"
        />
      </button>
      <Modal
        isOpen={open}
        onClose={() => setOpen(false)}
        title={visible ? 'Friends can see you' : 'Friends can’t see you'}
        variant="sheet"
        size="sm"
      >
        <ModalBody className="pb-6">
          <ul className="perm-sheet">
            {rows.map(({ key, label, Icon, on, action, hint }) => (
              <li key={key} className="perm-sheet__row">
                <span
                  className={`perm-sheet__icon${on ? '' : ' perm-sheet__icon--off'}`}
                  aria-hidden="true"
                >
                  <Icon size={20} />
                </span>
                <span className="perm-sheet__label">{label}</span>
                {on ? (
                  <span className="perm-sheet__on">
                    <Check size={16} aria-hidden="true" /> On
                  </span>
                ) : action ? (
                  <button
                    type="button"
                    className="maps-page__row-find"
                    onClick={action === 'ask' ? onAskLocation : openSettings}
                  >
                    Turn on
                  </button>
                ) : (
                  <span className="perm-sheet__off">{hint ?? 'Off'}</span>
                )}
              </li>
            ))}
          </ul>
        </ModalBody>
      </Modal>
    </>
  );
}
